// @ts-nocheck
// TODO: tighten types after migration
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../../config/api';
import { getAuthToken } from '../../utils/auth';
import './LearnWithAI.css';
import ChatView from './ChatView';
import FlashcardsView from './FlashcardsView';
import QuizView from './QuizView';
import ProgressView from './ProgressView';
import CurriculumPanel from './CurriculumPanel';
import TopicSelectionModal from './TopicSelectionModal';

function LearnWithAI() {
    const navigate = useNavigate();

    const [token, setToken] = useState('');
    const [topics, setTopics] = useState([]);
    const [selectedTopic, setSelectedTopic] = useState('');
    const [activeTab, setActiveTab] = useState('chat');
    const [loadingTopics, setLoadingTopics] = useState(true);
    const [error, setError] = useState('');

    // Topic selection modal
    const [showTopicModal, setShowTopicModal] = useState(false);
    const [topicModalDone, setTopicModalDone] = useState(false);

    // Trending topics (F3)
    const [trendingTopics, setTrendingTopics] = useState([]);

    // Draggable split divider (F4) — splitRatio is % for left/top panel
    const [splitRatio, setSplitRatio] = useState(() => {
        var saved = sessionStorage.getItem('lw_split_ratio');
        return saved ? parseFloat(saved) : 48;
    });
    const splitWrapperRef = useRef(null);
    const isDraggingRef = useRef(false);

    // Prefill for chat input from curriculum panel
    const [chatPrefill, setChatPrefill] = useState('');

    // Sync with site-level dark-mode class (toggled by Header.js Settings)
    const [isDark, setIsDark] = useState(
        document.body.classList.contains('dark-mode')
    );

    useEffect(() => {
        var observer = new MutationObserver(function() {
            setIsDark(document.body.classList.contains('dark-mode'));
        });
        observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
        return function() { observer.disconnect(); };
    }, []);

    // Auth guard
    useEffect(() => {
        var t = getAuthToken();
        if (!t) { navigate('/signin'); return; }
        setToken(t);
    }, [navigate]);

    // Fetch topics + trending (F3)
    useEffect(() => {
        if (!token) return;
        setLoadingTopics(true);
        setError('');

        var headers = { 'Authorization': 'Bearer ' + token };

        // Fetch topics and trending in parallel
        Promise.all([
            fetch(API_BASE_URL + '/learnwithai/topics').then(function(r) { return r.text(); }),
            fetch(API_BASE_URL + '/learnwithai/trending', { headers: headers })
                .then(function(r) { return r.text(); })
                .catch(function() { return ''; })
        ]).then(function(results) {
            var topicsText = results[0];
            var trendingText = results[1];

            // Parse topics
            var trimmed = topicsText ? topicsText.trim() : '';
            if (trimmed && trimmed !== 'undefined') {
                var data = JSON.parse(trimmed);
                if (data.success && data.data.length > 0) {
                    setTopics(data.data);
                    var savedTopic = sessionStorage.getItem('lw_topic');
                    if (savedTopic && data.data.find(function(t) { return t.slug === savedTopic; })) {
                        setSelectedTopic(savedTopic);
                        setTopicModalDone(true);
                    } else {
                        setSelectedTopic(data.data[0].slug);
                        setShowTopicModal(true);
                    }
                }
            }

            // Parse trending
            var tTrimmed = trendingText ? trendingText.trim() : '';
            if (tTrimmed && tTrimmed !== 'undefined') {
                try {
                    var tData = JSON.parse(tTrimmed);
                    if (tData.success && tData.data) setTrendingTopics(tData.data);
                } catch (e) {}
            }
        }).catch(function() {
            setError('Failed to load topics. Please try again.');
        }).finally(function() {
            setLoadingTopics(false);
        });
    }, [token]);

    // F4: Draggable split divider — works both horizontally (desktop) and vertically (mobile)
    var handleDividerPointerDown = useCallback(function(e) {
        e.preventDefault();
        isDraggingRef.current = true;
        var isMobile = window.innerWidth <= 768;
        document.body.style.cursor = isMobile ? 'row-resize' : 'col-resize';
        document.body.style.userSelect = 'none';

        var onMove = function(moveEvent) {
            if (!isDraggingRef.current || !splitWrapperRef.current) return;
            var rect = splitWrapperRef.current.getBoundingClientRect();
            var clientX = moveEvent.touches ? moveEvent.touches[0].clientX : moveEvent.clientX;
            var clientY = moveEvent.touches ? moveEvent.touches[0].clientY : moveEvent.clientY;
            var pct;
            if (isMobile) {
                pct = ((clientY - rect.top) / rect.height) * 100;
            } else {
                pct = ((clientX - rect.left) / rect.width) * 100;
            }
            pct = Math.max(40, Math.min(60, pct));
            setSplitRatio(pct);
        };

        var onUp = function() {
            isDraggingRef.current = false;
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
            document.removeEventListener('touchmove', onMove);
            document.removeEventListener('touchend', onUp);
            setSplitRatio(function(r) { sessionStorage.setItem('lw_split_ratio', String(r)); return r; });
        };

        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
        document.addEventListener('touchmove', onMove, { passive: false });
        document.addEventListener('touchend', onUp);
    }, []);

    var handleTopicModalSelect = function(topic) {
        setSelectedTopic(topic.slug);
        sessionStorage.setItem('lw_topic', topic.slug);
        setShowTopicModal(false);
        setTopicModalDone(true);
        // Prefill chat with topic greeting
        setChatPrefill('Tell me about ' + topic.displayName + ' — start from the basics');
    };

    if (!token) return null;

    // Determine if we show the split layout (curriculum + chat)
    var showSplit = activeTab === 'chat' && topicModalDone && !showTopicModal;

    return (
        <>
            <Helmet>
                <title>Learn with AI | Hari Vishnu</title>
                <meta name="description" content="AI-powered learning platform. Practice aptitude, learn concepts step by step, and get instant AI explanations." />
                <link rel="canonical" href="https://www.harivishnu.in/app-store/learnwithai" />
                <link href="https://fonts.googleapis.com/css2?family=Merriweather:wght@400;700&display=swap" rel="stylesheet" />
            </Helmet>

            {/* Topic selection modal */}
            {showTopicModal && topics.length > 0 && (
                <TopicSelectionModal
                    topics={topics}
                    defaultTopic={selectedTopic}
                    onSelect={handleTopicModalSelect}
                    trendingTopics={trendingTopics}
                />
            )}

            <div className={'lw-shell mx-auto my-[1rem] w-[min(96%,1500px)] overflow-hidden rounded-[14px] border border-slider bg-bg shadow-sm' + (isDark ? ' lw-dark' : '')}>
                {/* Views */}
                {error && (
                    <div className="lw-view lw-view-scroll p-[12px]">
                        <div className="lw-error rounded-[10px] border border-[#fecaca] bg-[#fff5f5] px-[12px] py-[10px] text-[#dc2626]">{error}</div>
                    </div>
                )}

                {!loadingTopics && selectedTopic && !error && topicModalDone && (
                    <div ref={splitWrapperRef} className={showSplit ? 'lw-split-wrapper relative grid min-h-[70vh] gap-[10px] md:grid-cols-[var(--split-ratio)_1fr] p-[10px]' : ('lw-view' + (activeTab === 'chat' ? ' lw-view-chat p-[10px]' : ' lw-view-scroll p-[10px]'))}>
                        {showSplit && (
                            <>
                                {/* Curriculum panel — left on desktop, top on mobile */}
                                <div className="lw-split-curriculum min-h-0 overflow-hidden rounded-[12px] border border-slider bg-bg" style={{ '--split-ratio': splitRatio + '%' }}>
                                    <CurriculumPanel
                                        topic={selectedTopic}
                                        token={token}
                                        onPrefillChat={function(text) { setChatPrefill(text); }}
                                    />
                                </div>

                                {/* Draggable divider (F4) — visible only on mobile */}
                                <div
                                    className="lw-split-divider mx-auto h-[8px] w-[64px] cursor-row-resize rounded-[999px] bg-slider md:absolute md:left-[var(--split-ratio)] md:top-1/2 md:h-[64px] md:w-[8px] md:-translate-x-1/2 md:-translate-y-1/2 md:cursor-col-resize"
                                    onMouseDown={handleDividerPointerDown}
                                    onTouchStart={handleDividerPointerDown}
                                />

                                {/* Chat panel — right on desktop, bottom on mobile */}
                                <div className="lw-split-chat min-h-0 overflow-hidden rounded-[12px] border border-slider bg-bg">
                                    <ChatView
                                        topic={selectedTopic}
                                        token={token}
                                        topics={topics}
                                        selectedTopic={selectedTopic}
                                        onTopicChange={function(slug) {
                                            setSelectedTopic(slug);
                                            sessionStorage.setItem('lw_topic', slug);
                                        }}
                                        loadingTopics={loadingTopics}
                                        prefillText={chatPrefill}
                                        onPrefillConsumed={function() { setChatPrefill(''); }}
                                        activeTab={activeTab}
                                        onTabChange={setActiveTab}
                                    />
                                </div>
                            </>
                        )}

                        {!showSplit && (
                            <>
                                {activeTab === 'chat'      && <ChatView topic={selectedTopic} token={token} topics={topics} selectedTopic={selectedTopic} onTopicChange={setSelectedTopic} loadingTopics={loadingTopics} activeTab={activeTab} onTabChange={setActiveTab} />}
                                {activeTab === 'flashcard' && <FlashcardsView topic={selectedTopic} token={token} />}
                                {activeTab === 'quiz'      && <QuizView topic={selectedTopic} token={token} />}
                                {activeTab === 'progress'  && <ProgressView topic={selectedTopic} token={token} />}
                            </>
                        )}
                    </div>
                )}

                {!loadingTopics && !selectedTopic && !error && (
                    <div className="lw-view lw-view-scroll p-[12px]">
                        <div className="lw-empty flex min-h-[220px] flex-col items-center justify-center rounded-[12px] border border-slider bg-bg text-center">
                            <span className="lw-empty-icon text-[2rem]">📚</span>
                            <p>No topics available yet.</p>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}

export default LearnWithAI;
